import { db } from "./firebase";
import { ref, get, query, orderByChild, update } from "firebase/database";
import { verifyAuth } from "./authFunction";

export const getCompaniesList = async () => {
  try {
    const authStatus = await verifyAuth();
    if (!authStatus.success) {
      return {
        success: false,
        error: "Unauthorized: Administrative clearance required.",
      };
    }
    const companiesRef = ref(db, "contacthq/companies");
    const companiesQuery = query(companiesRef, orderByChild("name"));
    const snapshot = await get(companiesQuery);
    if (!snapshot.exists()) {
      return { success: true, data: [] };
    }
    const companies = Object.entries(snapshot.val()).map(([id, val]) => ({
      id,
      ...val,
      userCount: val.users ? Object.keys(val.users).length : 0,
    }));

    return { success: true, data: companies };
  } catch (error) {
    console.error("🔥 Error fetching companies list:", error);
    return {
      success: false,
      message: error.message || "Failed to retrieve orchestrated companies.",
    };
  }
};

export const updateCompanyStatus = async (companyId, newStatus) => {
  try {
    const authStatus = await verifyAuth();
    if (!authStatus.success) {
      return {
        success: false,
        error: "Unauthorized: Administrative clearance required.",
      };
    }
    if (!companyId) throw new Error("Missing Company ID asset.");
    const companyRef = ref(db, `contacthq/companies/${companyId}`);
    await update(companyRef, {
      status: newStatus,
      statusUpdatedAt: new Date().toISOString(),
    });
    return {
      success: true,
      message: `Company status successfully synchronized to ${newStatus ? "Active" : "Suspended"}.`,
    };
  } catch (error) {
    console.error("🔥 Firebase Status Error:", error);
    return {
      success: false,
      message: error.message || "Failed to synchronize status.",
    };
  }
};

/**
 * Admin override: update a company's subscription / plan fields.
 * Writes under contacthq/companies/{companyId}/subscription
 */
export const updateCompanyPlan = async (companyId, subscriptionPayload) => {
  try {
    const authStatus = await verifyAuth();
    if (!authStatus.success) {
      return {
        success: false,
        error: "Unauthorized: Administrative clearance required.",
      };
    }
    if (!companyId) throw new Error("Missing Company ID asset.");
    if (!subscriptionPayload?.plan) throw new Error("Plan selection is required.");

    const companyRef = ref(db, `contacthq/companies/${companyId}`);
    const snap = await get(companyRef);
    if (!snap.exists()) {
      throw new Error("Company record not found.");
    }

    const existing = snap.val()?.subscription || {};
    const nextSubscription = {
      ...existing,
      plan: subscriptionPayload.plan,
      billingCycle:
        subscriptionPayload.billingCycle ||
        existing.billingCycle ||
        "monthly",
      formLimit:
        subscriptionPayload.formLimit !== undefined &&
        subscriptionPayload.formLimit !== null
          ? Number(subscriptionPayload.formLimit)
          : existing.formLimit || 1,
      expiresAt: subscriptionPayload.expiresAt || existing.expiresAt || null,
      price:
        subscriptionPayload.price !== undefined
          ? Number(subscriptionPayload.price)
          : existing.price ?? null,
      updatedAt: new Date().toISOString(),
      updatedBy: "admin",
    };

    await update(companyRef, {
      subscription: nextSubscription,
      planUpdatedAt: new Date().toISOString(),
    });

    return {
      success: true,
      data: nextSubscription,
      message: `Plan updated to ${nextSubscription.plan}.`,
    };
  } catch (error) {
    console.error("🔥 Firebase Plan Update Error:", error);
    return {
      success: false,
      message: error.message || "Failed to update company plan.",
    };
  }
};
